import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { MyAccount } from '../../../../models/my-account';

import { AccountService } from '../../../../services/account/account.service';
import { LocationService } from '../../../../services/location/location.service';
import { ApiService } from '../../../../services/api/api.service';
import { CallbackResult, CallbackResultStatus } from '../../../../utils';
import {
  SetUpTwoFactorAuthenticationModalComponent
} from './modals/set-up-two-factor-authentication-modal/set-up-two-factor-authentication-modal.component';
import { SimpleMessageModalComponent } from '../../../../components/modals/simple-message-modal/simple-message-modal.component';

@Component({
  selector: 'app-account-settings',
  templateUrl: './account-settings.component.html',
  styleUrls: ['./account-settings.component.css']
})
export class AccountSettingsComponent implements OnInit {

  public myAccount: MyAccount;

  public previousPassword = '';
  public newPassword = '';
  public newPasswordConfirm = '';

  public accountBasicInfoErrorMessage = '';
  public accountBasicInfoErrors: any = {};
  public accountBasicInfoSuccessMessage = '';
  public accountPasswordErrorMessage = '';
  public accountPasswordErrors: any = {};
  public accountPasswordSuccessMessage = '';

  public loadingAccount = false;
  public loadingAccountBasicInfo = false;
  public loadingAccountPassword = false;
  public loadingDisableTwoFactorAuth = false;

  public serverError: number = null;

  public callbackResult: CallbackResult = null;

  public readonly CallbackResultStatus = CallbackResultStatus;

  constructor(
    public apiService: ApiService,
    public locationService: LocationService,
    private accountService: AccountService,
    private modalService: NgbModal,
  ) {}

  ngOnInit() {
    this.loadAccount();
  }

  /**
   * Load the latest account info from the API. We do this before pulling the
   * stored account info from accountService to ensure that it is up to date.
   */
  public loadAccount() {
    this.loadingAccount = true;

    // Clear feedback messages.
    this.clearFeedbackMessages();

    // We don't need to handle promise rejections here; any issues loading the
    // user's account are handled at the app component level.
    this.accountService
      .loadMyAccountInfo()
      .then(() => {
        this.myAccount = this.accountService.getMyAccount();
      })
      .finally(() => { this.loadingAccount = false; });
  }

  /**
   * Update the user's basic info according to the provided values.
   */
  public updateBasicInfo() {
    this.loadingAccountBasicInfo = true;

    this.clearFeedbackMessages();

    this.accountService
      .updateMyAccount(this.myAccount)
      .then((updatedAccount: MyAccount) => {
        this.myAccount = updatedAccount;
        this.accountBasicInfoSuccessMessage = 'Account updated successfully.';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.accountBasicInfoErrors = reason.error;
          this.accountBasicInfoErrorMessage = 'Please correct the errors above and try again.';
        } else if (reason.status === 403) {
          this.accountBasicInfoErrorMessage = 'You do not have permission ' +
                                              'to modify your account info.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingAccountBasicInfo = false;
      });
  }

  /**
   * Update the user's password according to the provided values.
   */
  public updatePassword() {
    this.loadingAccountPassword = true;

    this.clearFeedbackMessages();

    this.previousPassword = this.previousPassword.trim()
    this.newPassword = this.newPassword.trim()
    this.newPasswordConfirm = this.newPasswordConfirm.trim()

    // Check for field errors, namely missing required fields.
    if (this.previousPassword === '') {
      this.accountPasswordErrors.previous_password = 'This field is required.';
    }
    if (this.newPassword === '') {
      this.accountPasswordErrors.new_password = 'This field is required.';
    }
    if (this.newPasswordConfirm === '') {
      this.accountPasswordErrors.new_password_confirm = 'This field is required.';
    }

    if (Object.entries(this.accountPasswordErrors).length !== 0) {
      this.accountPasswordErrorMessage = 'Please correct the errors above and try again.';
      this.loadingAccountPassword = false;
      return;
    }

    // Check that the new password confirmation matches (this is not done by the API).
    if (this.newPassword !== this.newPasswordConfirm) {
      this.accountPasswordErrorMessage = 'New passwords do not match.';
      this.loadingAccountPassword = false;
      return;
    }

    this.accountService
      .changeMyAccountPassword(this.previousPassword, this.newPassword)
      .then(() => {
        this.accountPasswordSuccessMessage = 'Password updated successfully.';

        // Clear password input fields
        this.previousPassword = '';
        this.newPassword = '';
        this.newPasswordConfirm = '';
      })
      .catch(reason => {
        if (reason.status === 400) {
          this.accountPasswordErrorMessage = reason.error;
        } else if (reason.status === 403) {
          this.accountPasswordErrorMessage = 'You do not have permission ' +
                                             'to modify your password.';
        } else {
          this.serverError = reason.status;
        }
      })
      .finally(() => {
        this.loadingAccountPassword = false;
      });
  }

  /**
   * Reset the password input fields.
   */
  public resetPasswordInput() {
    this.previousPassword = '';
    this.newPassword = '';
    this.newPasswordConfirm = '';
  }

  public openSetUpTwoFactorAuthModal() {
    this.clearFeedbackMessages();

    const modalRef = this.modalService.open(
      SetUpTwoFactorAuthenticationModalComponent
    );
    modalRef.componentInstance.savedCallback = (result: CallbackResult) => {
      this.callbackResult = result;
      this.loadAccount();
    }
  }

  public openDisableTwoFactorAuthModal() {
    this.clearFeedbackMessages();

    const modalRef = this.modalService.open(SimpleMessageModalComponent);
    modalRef.componentInstance.headerText = 'Disable Two-Factor Authentication';
    modalRef.componentInstance.messageText = 'Are you sure you want to disable ' +
      'two-factor authentication? An authenticator code will no longer be ' +
      'required when logging into the Staff Site.';
    modalRef.componentInstance.messageAlertClass = 'alert-danger';
    modalRef.componentInstance.hasConfirmation = true;
    modalRef.componentInstance.confirmedCallback = () => {
      this.loadingDisableTwoFactorAuth = true;

      this.accountService
        .removeOTP()
        .then(() => {
          this.loadAccount();
        })
        .catch(reason => {
          if (reason.status === 400) {
            this.callbackResult = {
              text: reason.error,
              status: CallbackResultStatus.error
            };
          } else if (reason.status === 403) {
            this.callbackResult = {
              text: 'You do not have permission to disable two-factor authentication.',
              status: CallbackResultStatus.error
            };
          } else {
            this.serverError = reason.status;
          }
        })
        .finally(() => { this.loadingDisableTwoFactorAuth = false; });
    }
  }

  private clearFeedbackMessages() {
    this.accountBasicInfoErrorMessage = '';
    this.accountBasicInfoErrors = {};
    this.accountBasicInfoSuccessMessage = '';

    this.accountPasswordErrorMessage = '';
    this.accountPasswordErrors = {};
    this.accountPasswordSuccessMessage = '';

    this.callbackResult = null;
  }
}
